import { useCallback } from 'react';

import type { SequenceFace } from '~/components/Sequence/SequenceNodeFaces';
import { updateWithToast } from '@/clientOnlyUtils/clientUtils';
import { useRouterQuery } from '@/hooks/useRouterQuery';
import {
  useSequenceCompleteTaskMutation,
  useSequenceRemoveMutation,
  useSequenceRestartMutation,
} from '@/hooks/trpc/useSequenceMutation';
import { openContactDetailQuery } from '~/components/ContactCard/ContactCard';

const faceName = (f: SequenceFace) => [f.firstName, f.lastName].filter(Boolean).join(' ') || 'contact';

/**
 * The per-face actions shared by every place enrolled people are shown (the sequence editor pile AND the
 * full journey graph): open the contact's detail panel IN PLACE (updates `?id=` on the current route, no
 * navigation), restart their journey, or remove them. Keeps the wiring in one spot so both surfaces behave
 * identically.
 */
export function useSequenceFaceActions(teamId: string, sequenceId: string) {
  const [, setQuery] = useRouterQuery();
  const restartMutation = useSequenceRestartMutation();
  const removeMutation = useSequenceRemoveMutation();
  const completeTaskMutation = useSequenceCompleteTaskMutation();

  const onOpenContact = useCallback(
    (face: SequenceFace) => setQuery((q) => openContactDetailQuery(q, { contactId: face.id })),
    [setQuery],
  );

  const onRestart = useCallback(
    (face: SequenceFace) => {
      void updateWithToast({
        loading: `Restarting ${faceName(face)}…`,
        success: 'Journey restarted',
        mutationFnAsync: () => restartMutation.mutateAsync({ teamId, sequenceId, contactIds: [face.id] }),
      });
    },
    [restartMutation, teamId, sequenceId],
  );

  const onRemove = useCallback(
    (face: SequenceFace) => {
      void updateWithToast({
        loading: `Removing ${faceName(face)}…`,
        success: 'Removed from sequence',
        mutationFnAsync: () => removeMutation.mutateAsync({ teamId, sequenceId, contactIds: [face.id] }),
      });
    },
    [removeMutation, teamId, sequenceId],
  );

  // Mark a parked call/reminder to-do done → advance the person past the manual step. Keyed by the face's
  // wait node so it completes exactly the task shown (call vs reminder), and — where the caller knows which
  // authored step it drew the pile for — by that step's address too, so a sequence with more than one
  // manual step completes the to-do the rep actually clicked rather than every arm on that kind of lane.
  // The whole-journey graph draws in node space with no step address, and omits it.
  const onCompleteTask = useCallback(
    (face: SequenceFace, stepKey?: string) => {
      void updateWithToast({
        loading: `Completing ${faceName(face)}…`,
        // Report what actually happened: the delete is scoped to an arm still parked at that address, so a
        // stale pile (someone else marked it done, or the sequence was edited under the poll) clears
        // nothing — claiming "sequence advanced" there would be a lie the rep acts on.
        success: (data) =>
          data != null && data.completed === 0
            ? 'Nothing to mark done — that to-do already moved on'
            : 'Marked done — sequence advanced',
        mutationFnAsync: () =>
          completeTaskMutation.mutateAsync({ teamId, sequenceId, contactId: face.id, nodeId: face.nodeId, stepKey }),
      });
    },
    [completeTaskMutation, teamId, sequenceId],
  );

  return { onOpenContact, onRestart, onRemove, onCompleteTask };
}
